import React from 'react'

import { Box, BoxProps, withCitrusTheme } from '@gousto-internal/citrus-react'

import pauseLight from './assets/pause-light.svg'
import playLight from './assets/play-light.svg'

const onSpaceOrEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      callback(event)
    }
  }

type CWCPlayPauseButtonProps = {
  /** Whether the video is currently playing — drives the icon and the Play/Pause verb. */
  isPlaying: boolean
  /** Invoked on click and on Enter/Space. The owner (e.g. CWCVideoCard) decides what toggling does. */
  onToggle: () => void
  /**
   * The card's title (step or recipe name). When several video cards share this control, it's
   * appended to the accessible name so screen-reader / keyboard users can tell which card they're
   * toggling (the `<video>` itself is `aria-hidden`).
   */
  mediaLabel?: string
}

const StyledButton = ({ btnIcon, ...props }: BoxProps & { btnIcon: string }) => {
  const Component = withCitrusTheme(Box, () => ({
    cursor: 'pointer',
    background: `center/contain  no-repeat url(${btnIcon})`,
  }))
  return <Component {...props} />
}

/**
 * Presentational play/pause control: it renders the icon + accessible name for the current
 * `isPlaying` state and reports intent via `onToggle` on click / Enter / Space. It owns no video or
 * tracking logic — that's injected by the caller (Open for extension, closed for modification), so
 * the same control can drive any toggle.
 */
export function CWCPlayPauseButton({ isPlaying, onToggle, mediaLabel }: CWCPlayPauseButtonProps) {
  const action = isPlaying ? 'Pause' : 'Play'
  const ariaLabel = mediaLabel ? `${action} video for ${mediaLabel}` : `${action} video`

  return (
    <StyledButton
      aria-label={ariaLabel}
      borderRadius="round"
      btnIcon={isPlaying ? pauseLight : playLight}
      height="32px"
      onClick={onToggle}
      onKeyDown={onSpaceOrEnter(onToggle)}
      role="button"
      width="32px"
      tabIndex={0}
    ></StyledButton>
  )
}
